/**
 * 日历周视图。
 *
 * 周日为第一列（吉兰丹习惯）。休息日以斜纹标记，休哪天读设置里的营业时间，
 * 但仍允许排入（旺季会加班）。这是本页最重要的本地化。
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { api, type Appointment, type Me } from '../lib/api'
import { APPT_STATUS_ZH, WEEKDAYS_SHORT, addDays, dateKey, time as fmtTime } from '../lib/format'
import { Badge, Button, Card, Chip, Skeleton, cx } from '../components/ui'
import { AppointmentDrawer } from '../features/AppointmentDrawer'

const HOURS = Array.from({ length: 10 }, (_, i) => 9 + i) // 09:00–18:00

export function CalendarPage({ me }: { me: Me }) {
  const navigate = useNavigate()
  const [editing, setEditing] = useState<Appointment | null>(null)
  const [anchor, setAnchor] = useState(() => new Date())
  const [view, setView] = useState<'week' | 'day'>('week')
  const [apptOpen, setApptOpen] = useState(false)

  // 周日为一周起点
  const weekStart = useMemo(() => {
    const d = new Date(anchor)
    const dow = new Date(d.getTime() + 8 * 3600_000).getUTCDay()
    return addDays(d, -dow)
  }, [anchor])

  const days = useMemo(
    () =>
      Array.from({ length: view === 'week' ? 7 : 1 }, (_, i) =>
        view === 'week' ? addDays(weekStart, i) : anchor,
      ),
    [weekStart, anchor, view],
  )

  const from = days[0]!
  const to = addDays(days[days.length - 1]!, 1)

  const q = useQuery({
    queryKey: ['appointments', dateKey(from), dateKey(to)],
    queryFn: () =>
      api.get<{
        items: Appointment[]
        businessHours: Record<string, { open: string; close: string } | null>
        closedDates: string[]
        openDates: string[]
      }>(`/api/appointments?from=${from.toISOString()}&to=${to.toISOString()}`),
  })

  const byDay = useMemo(() => {
    const m = new Map<string, Appointment[]>()
    for (const a of q.data?.items ?? []) {
      const k = a.dateKey
      if (!m.has(k)) m.set(k, [])
      m.get(k)!.push(a)
    }
    return m
  }, [q.data])

  const todayKey = dateKey(new Date())
  const hours = q.data?.businessHours ?? {}
  const closed = new Set(q.data?.closedDates ?? [])
  const open = new Set(q.data?.openDates ?? [])

  const isRest = (d: Date) => {
    const k = dateKey(d)
    if (open.has(k)) return false
    if (closed.has(k)) return true
    const dow = new Date(d.getTime() + 8 * 3600_000).getUTCDay()
    return hours[String(dow)] == null
  }

  return (
    <div className="mx-auto max-w-full">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-[28px] font-bold">日历</h1>

        <div className="flex items-center gap-1">
          <Button
            size="sm"
            icon={<ChevronLeft size={18} />}
            aria-label="上一周"
            onClick={() => setAnchor((d) => addDays(d, view === 'week' ? -7 : -1))}
          />
          <Button size="sm" onClick={() => setAnchor(new Date())}>
            本{view === 'week' ? '周' : '日'}
          </Button>
          <Button
            size="sm"
            icon={<ChevronRight size={18} />}
            aria-label="下一周"
            onClick={() => setAnchor((d) => addDays(d, view === 'week' ? 7 : 1))}
          />
        </div>

        <div className="flex gap-2">
          <Chip active={view === 'week'} onClick={() => setView('week')}>
            周
          </Chip>
          <Chip active={view === 'day'} onClick={() => setView('day')}>
            日
          </Chip>
        </div>

        <div className="flex-1" />
        <Button variant="primary" icon={<Plus size={20} />} onClick={() => setApptOpen(true)}>
          预约
        </Button>
      </div>

      {q.isLoading ? (
        <Skeleton className="h-[32rem]" />
      ) : (
        <Card bodyClassName="p-0! sm:p-0!">
          <div className="overflow-x-auto">
            <div className="min-w-[52rem]">
              {/* 列头 */}
              <div
                className="grid border-b border-line"
                style={{ gridTemplateColumns: `4rem repeat(${days.length}, minmax(0,1fr))` }}
              >
                <div />
                {days.map((d) => {
                  const k = dateKey(d)
                  const dow = new Date(d.getTime() + 8 * 3600_000).getUTCDay()
                  const rest = isRest(d)
                  return (
                    <div
                      key={k}
                      className={cx(
                        'border-l border-line px-2 py-2 text-center',
                        k === todayKey && 'border-t-[3px] border-t-brand-600',
                      )}
                    >
                      <div className="text-[15px] text-ink-2">{WEEKDAYS_SHORT[dow]}</div>
                      <div
                        className={cx(
                          'tnum text-[19px] font-bold',
                          k === todayKey && 'text-brand-700',
                        )}
                      >
                        {k.slice(-2)}
                      </div>
                      {rest && (
                        <div className="text-[12px] text-ink-3">
                          {closed.has(k) ? '假期' : '休息'}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* 时段网格 */}
              <div
                className="relative grid"
                style={{ gridTemplateColumns: `4rem repeat(${days.length}, minmax(0,1fr))` }}
              >
                <div>
                  {HOURS.map((h) => (
                    <div
                      key={h}
                      className="tnum h-16 border-b border-line pr-2 pt-1 text-right text-[14px] text-ink-3"
                    >
                      {String(h).padStart(2, '0')}:00
                    </div>
                  ))}
                </div>

                {days.map((d) => {
                  const k = dateKey(d)
                  const rest = isRest(d)
                  const list = byDay.get(k) ?? []
                  return (
                    <div
                      key={k}
                      className={cx(
                        'relative border-l border-line',
                        rest && 'gb-hatch opacity-90',
                      )}
                    >
                      {HOURS.map((h) => (
                        <div key={h} className="h-16 border-b border-line" />
                      ))}

                      {list.map((a) => {
                        const t = new Date(a.startsAt)
                        const local = new Date(t.getTime() + 8 * 3600_000)
                        const mins = local.getUTCHours() * 60 + local.getUTCMinutes() - 9 * 60
                        if (mins < 0 || mins > 10 * 60) return null
                        const top = (mins / 60) * 64
                        const height = Math.max((a.durationMin / 60) * 64 - 2, 28)
                        const tone =
                          a.status === 'ARRIVED'
                            ? 'border-l-ok bg-ok-bg'
                            : a.status === 'CONFIRMED'
                              ? 'border-l-brand-600 bg-brand-50'
                              : a.status === 'NO_SHOW'
                                ? 'border-l-bad bg-bad-bg border-dashed'
                                : 'border-l-line-strong bg-white'
                        return (
                          <button
                            key={a.id}
                            // 点块看的是这次约见本身（改期/取消/删除），
                            // 跳工单反而绕远 —— 工单从预约详情里还能再点进去
                            onClick={() => setEditing(a)}
                            style={{ top, height }}
                            className={cx(
                              'absolute inset-x-1 overflow-hidden rounded-sm border border-l-[3px] px-2 py-1 text-left',
                              tone,
                            )}
                          >
                            <div className="truncate text-[14px] font-semibold">{a.displayName}</div>
                            <div className="tnum truncate text-[12px] text-ink-2">
                              {fmtTime(a.startsAt)} {a.purpose ?? a.caseStep?.title ?? ''}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3 text-[15px] text-ink-2">
        <span className="flex items-center gap-1.5">
          <span className="gb-hatch inline-block size-4 rounded-[3px] border border-line" />
          休息日（周五、周六）
        </span>
        <Badge tone="ok">{APPT_STATUS_ZH.ARRIVED}</Badge>
        <Badge tone="brand">{APPT_STATUS_ZH.CONFIRMED}</Badge>
        <Badge tone="bad">{APPT_STATUS_ZH.NO_SHOW}</Badge>
      </div>

      <AppointmentDrawer open={apptOpen} onClose={() => setApptOpen(false)} />

      <AppointmentDrawer
        open={editing !== null}
        onClose={() => setEditing(null)}
        appointment={editing}
        isOwner={me.user.role === 'OWNER'}
      />
    </div>
  )
}
